import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../../hooks/useDateSort';

export interface DeadClickDailyRow {
  date: string;
  totalClickCount: number;
  uniqueUserCount: number;
  deadClickRate: number;
}

export interface DeadClickTableProps {
  /** 상단에 표시할 실험 영역 라벨 (실험 영역 1, 2, 3 등) */
  experimentLabel?: string;
  /** 테이블 위에 표시할 이벤트명(영역명) */
  title?: string;
  /** 영역 설명 이미지 URL  */
  imageSrc?: string;
  /** 이미지 가로(px). 미지정 시 320 */
  imageWidth?: number;
  /** 일별 데드클릭 통계 */
  rows: DeadClickDailyRow[];
  /** 데드 클릭률 툴팁 설명 */
  rateTooltip?: string;
}

const DEAD_CLICK_RATE_TOOLTIP =
  '데드 클릭 세션 수 / 룰렛 진입 세션 수 비율입니다.';

function formatDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-');
    if (y && m && d) return `${y}.${m}.${d}`;
  } catch {
    // no-op
  }
  return dateStr;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function DeadClickTable({
  experimentLabel,
  title,
  imageSrc,
  imageWidth = 320,
  rows,
  rateTooltip = DEAD_CLICK_RATE_TOOLTIP,
}: DeadClickTableProps) {
  const { sorted, sortDirection, toggleSort } = useDateSort(rows, (r) => r.date, 'desc');

  return (
    <Box sx={{ width: '100%' }}>
      {(experimentLabel || title) && (
        <Box
          sx={{
            mb: 2.5,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              borderRadius: 0.5,
              border: '1px solid #e5e7eb',
              textAlign: 'center',
              maxWidth: 260,
              width: '100%',
            }}
          >
            {experimentLabel && (
              <Typography
                variant="body2"
                sx={{ color: '#4b5563', mb: title ? 0.5 : 0 }}
              >
                {experimentLabel}
              </Typography>
            )}
            {title && (
              <Box sx={{ display: 'inline-flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: '#e5e7eb',
                    border: '1px solid #d4d4d8',
                    maxWidth: '100%',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      color: '#111827',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {imageSrc && (
        <Box
          component="img"
          src={imageSrc}
          alt=""
          sx={{
            display: 'block',
            mx: 'auto',
            mb: 4,
            width: imageWidth,
            height: 220,
            objectFit: 'contain',
          }}
        />
      )}
      <TableContainer
        sx={{
          width: '100%',
          border: '1px solid #e5e7eb',
          borderRadius: 0.2,
          overflow: 'hidden',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            '& th, & td': { borderBottom: '1px solid #e5e7eb' },
            '& thead th': { borderBottom: '1px solid #d1d5db', backgroundColor: '#f9fafb' },
            '& tbody tr:last-of-type td': { borderBottom: 'none' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                onClick={toggleSort}
                sx={{
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { opacity: 0.8 },
                }}
              >
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  날짜
                  <Box component="span" sx={{ fontSize: '0.75rem', color: '#666' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </Box>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                전체 데드 클릭 횟수
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                데드 클릭 사용자 수
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                데드 클릭률{' '}
                <Tooltip title={rateTooltip} placement="top" arrow>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, verticalAlign: 'middle', color: '#6b7280' }}
                  />
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#6b7280' }}>
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => (
                <TableRow key={row.date}>
                  <TableCell align="center">{formatDate(row.date)}</TableCell>
                  <TableCell align="center">{formatNumber(row.totalClickCount)}</TableCell>
                  <TableCell align="center">{formatNumber(row.uniqueUserCount)}</TableCell>
                  <TableCell align="center">{formatPercent(row.deadClickRate)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default DeadClickTable;
